import { from } from "rxjs";
import { APIResource, APIMethod } from "libs/utils/http/decorator/UrlDecorator";
import { apiMethodContext } from "libs/utils/http/builder/UrlConfig";
import { CloudConsoleClient } from "libs/utils/http/client/HttpClient";
import { INoAuthResponse } from "../components/NoAuthConfiguration";
import "reflect-metadata";

export type IdentityApplicationEnvironmentType =
  | "trimble-pre-prod"
  | "trimble-prod";

export interface IdentityApplicationPublishedGateway {
  consumerKey: string;
  consumerSecret: string[];
  grantTypes?: string[];
  logoutUri?: string[];
  organisationName: IdentityApplicationEnvironmentType;
  redirectUri?: string[];
  enforceScopeValidation?: boolean;
}



export interface IdentityApplicationSPMetadata {
  assertionConsumerUrl: string;
  x509RequestSigningCert?: string;
}

export interface IdentityApplicationType {
  id: string;
  name: IdentityApplicationTypes;
}

export interface ApiService{
  basePath: string;
}

export interface IdentityApplication {
  applicationId: string;
  applicationName: string;
  applicationType?: IdentityApplicationType;
  displayName?: string;
  description?: string;
  organisationName: IdentityApplicationEnvironmentType;
  applicationGatewayResponses?: IdentityApplicationPublishedGateway[];
  samlSpMetadata?: IdentityApplicationSPMetadata;
  userId?: string;
  isOwnedByUser?: Boolean;
  createdBy?: string;
  consumerKey: string;
  created?: string | Date;
  service?: ApiService; 
}

export interface IdentityApplicationCreateRequest {
  name?: string;
  displayName?: string;
  description?: string;
  applicationType: IdentityApplicationTypes;
  basePath?: string;
  grantTypes?: string[];
  redirectUri?: string[];
  logoutUri?: string[];
}

export interface IdentityApplicationUpdateRequest {
  name: string;
  displayName?: string;
  description?: string;
}

export interface IdentityApplicationPublishRequest {
  grantTypes?: string[];
  redirectUri?: string[];
  logoutUri?: string[];
  enforceScopeValidation?: true;
}

export interface IVersionDeployment {
  specFile: string;
  specId: string;
  deployments: IVersionDeploymentResponse[]; 
}
export interface IVersionDeploymentResponse {
  deploymentId?: string;
  backendUrl?: string;
  deployedUrl?: string;
  timeout?: number;
  deployStatus?: string;
  provider?: string;
  type?: string;
  location?: string;
  apiThrottling?: string | null;
  noAuth?: INoAuthResponse | null;
}
//NOTE: only two application types are supported for now (application, resource-server)
export type IdentityApplicationTypes =
  | "application"
  | "APPLICATION"
  | "RESOURCE_SERVER"
  | "resource_server";

const formatError = (error: any, defaultMessage: string) =>
  error?.response?.data?.code !== undefined && error.response.data.message
    ? error.response.data.message
    : defaultMessage;
export type IdentityApplicationPublishType = "publish" | "update";

@APIResource(CloudConsoleClient, "publisher/v2/applications")
class ApiServiceFactory {
  @APIMethod("", {
    options: {
      noTrailingSlash: true,
    },
  })
  public createApi(
    teamId: Record<string, string>,
    request?: IdentityApplicationCreateRequest
  ) {
    return from(
      CloudConsoleClient.post<any>(apiMethodContext(arguments), request, {
        params: {
          ...teamId,
        },
      }).then((value) => value.data)
    );
  }

  @APIMethod("/:consumerKey", {
    options: {
      noTrailingSlash: true,
    },
  })
  public delete(
   consumerKey: string
  ) {
    return from(
    CloudConsoleClient.delete<any>(apiMethodContext(arguments),{
        params: {
          consumerKey
        },
      }).then((value) => value.data)
    );
  }

  @APIMethod("", {
    options: {
      noTrailingSlash: true,
    },
  })
  public getAllApplication(gatewayName: string, applicationType: string) {
    return from(
      CloudConsoleClient.get<IdentityApplication[]>(
        apiMethodContext(arguments),
        {
          params: {
            gatewayName,
            includeEmailAddress: true,
            type: applicationType,
          },
        }
      ).then((value) => value.data)
    );
  }

  @APIMethod("/:consumerKey/version", {
    options: {
      noTrailingSlash: true,
    },
  })
  public createNewVersion(consumerKey: string, version: string) {
    return from(
      CloudConsoleClient.post<any>(
        apiMethodContext(arguments),
        { version: "v" + version },
        {
          params: {
            consumerKey,
          },
        }
      ).then((value) => value.data)
    );
  }

  @APIMethod("/:resourceAppId/versions/:version/deploy", {
    options: {
      noTrailingSlash: true,
    },
  })
  public createVersionDeployment(
    resourceAppId: string,
    version: string,
    targetUrl: string,
    region: string,
    timeout: number
  ) {
    return from(
      CloudConsoleClient.post<any>(
        apiMethodContext(arguments),
        {
          backendUrl: targetUrl,
          location: region,
          type: region === "global" ? "GLOBAL" : "REGION",
          timeout: timeout,
        },
        {
          params: {
            resourceAppId,
            version,
          },
        }
      ).then((value) => value.data)
    );
  }

  @APIMethod("/:consumerKey/version/:version", {
    options: {
      noTrailingSlash: true,
    },
  })
  public getVersionDeployment(consumerKey: string, version: string) {
    return from(
      CloudConsoleClient.get<IVersionDeploymentResponse[]>(
        apiMethodContext(arguments),
        {
          params: {
            consumerKey,
            version,
          },
        }
      ).then((value) => value.data)
    );
  }

    @APIMethod("/:consumerKey/version/:version/spec", {
    options: {
      noTrailingSlash: true,
    },
  })
  public getVersionSpec(consumerKey: string, version: string) {
    return from(
      CloudConsoleClient.get<IVersionDeploymentResponse[]>(
        apiMethodContext(arguments),
        {
          params: {
            consumerKey,
            version,
          },
        }
      ).then((value) => value.data)
    );
  }

  @APIMethod("/deployment/:deploymentId/noauth", {
    options: {
      noTrailingSlash: true,
    },
  })
  public createNoAuthConfiguration(deploymentId: string, request: any) {
    return from(
      CloudConsoleClient.post<any>(apiMethodContext(arguments), request, {
        params: {
          deploymentId,
        },
      }).then((value) => value.data)
    );
  }

  @APIMethod("/deployment/:deploymentId/noauth/:noAuthId", {
    options: {
      noTrailingSlash: true,
    },
  })
  public deleteNoAuthConfiguration(deploymentId: string, noAuthId: any) {
    return from(
      CloudConsoleClient.delete<any>(apiMethodContext(arguments), {
        params: {
          deploymentId,
          noAuthId,
        },
      }).then((value) => value.data)
    );
  }

  @APIMethod("/deployment/:deploymentId", {
    options: {
      noTrailingSlash: true,
    },
  })
  public deleteDeployment(deploymentId?: string) {
    return from(
      CloudConsoleClient.delete<any>(apiMethodContext(arguments), {
        params: {
          deploymentId,
        },
      }).then((value) => value.data)
    );
  }

    @APIMethod("/deployment/:deploymentId", {
    options: {
      noTrailingSlash: true,
    },
  })
  public updateDeployment( request: any, deploymentId?: string) {
    return from(
      CloudConsoleClient.put<any>(apiMethodContext(arguments),request, {
        params: {
          deploymentId,
        },
      }).then((value) => value.data)
    );
  }

  @APIMethod("/:consumerKey/version/:version", {
    options: {
      noTrailingSlash: true,
    },
  })
  public deleteVersion(consumerKey: string, version: string) {
    return from(
      CloudConsoleClient.delete<any>(apiMethodContext(arguments), {
        params: {
          consumerKey,
          version,
        },
      }).then((value) => value.data)
    );
  }

  @APIMethod("/:consumerKey/version/:version/spec", {
    options: {
      noTrailingSlash: true,
    },
  })
  public uploadSpec(consumerKey: string, version: string, payload: any) {
    return from(
      CloudConsoleClient.post<any>(apiMethodContext(arguments), payload, {
        params: {
          consumerKey,
          version,
        },
      }).then((value) => value.data)
    );
  }


    @APIMethod("/:consumerKey/version/:version/spec/:specId", {
    options: {
      noTrailingSlash: true,
    },
  })
  public deleteSpec(consumerKey: string, version: string, specId: string | null) {
    return from(
      CloudConsoleClient.delete<any>(apiMethodContext(arguments),{
        params: {
          consumerKey,
          version,
          specId
        },
      }).then((value) => value.data)
    );
  }

      @APIMethod("/:consumerKey/scopes")
      public addScope(consumerKey: string, scope: string, consentMessage: string, developerDocumentation: string, alwaysShowConsent: boolean) {
        return from(
          CloudConsoleClient.put<any>(apiMethodContext(arguments),{
            "scope": scope,
            "consentMessage": consentMessage,
            "developerDocumentation": developerDocumentation,
            "alwaysShowConsent": alwaysShowConsent
          },{
            params: {
              consumerKey
            },
          }).then((value) => value.data)
        );
      }
  
      @APIMethod("/:consumerKey/scopes",  {
          options: {
              noTrailingSlash: true
          }
      })
      public getScopes(consumerKey: string) {
        return from(
          CloudConsoleClient.get<any>(apiMethodContext(arguments), {
            params: {
              consumerKey          },
          }).then((value) => value.data)
        );
      }

    @APIMethod("/:consumerKey/scopes/update")
    public updateScope(consumerKey: string, scope: string, consentMessage: string, developerDocumentation: string, alwaysShowConsent: boolean) {
      return from(
        CloudConsoleClient.put<any>(apiMethodContext(arguments),{
          "scope": scope,
          "consentMessage": consentMessage,
          "developerDocumentation": developerDocumentation,
          "alwaysShowConsent": alwaysShowConsent
        },{
          params: {
            consumerKey
          },
        }).then((value) => value.data)
      );
    }

}

const ApiService = new ApiServiceFactory();

export default ApiService;
